import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Container,
  Fade,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { CachePolicies } from "use-http";

import { getConfig } from "config/config";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";

const config = getConfig();

const { JANZU_URL } = config;

interface LabelerMetrics {
  average_elapsed_screen_time_ms: number;
  label_count: number;
}

export function AdminLabelerMetricsPage() {
  const [labelerMetrics, setLabelerMetrics] = useState<LabelerMetrics>();
  const [refreshed, setRefreshed] = useState(false);
  const [refreshedTimer, setRefreshedTimer] = useState<number | undefined>();
  const [timeAgoHours, setTimeAgoHours] = useState<number>(24);

  const labelerMetricsUrl = `${JANZU_URL}device-api/metrics/labelers?type=average_elapsed_screen_time_ms&type=label_count&from=${timeAgoHours}h`;

  const {
    get: getLabelerMetricsData,
    data: labelerMetricsData,
    error,
  } = useFetchCustom(labelerMetricsUrl, { cachePolicy: CachePolicies.NO_CACHE });

  useEffect(() => {
    if (!error && labelerMetricsData) {
      setLabelerMetrics(labelerMetricsData);
    }
  }, [labelerMetricsData, error]);

  const handleRefresh = () => {
    // fetch data
    getLabelerMetricsData();
    // clear and set timer for refresh icon becoming checkmark
    clearTimeout(refreshedTimer);
    setRefreshed(true);
    const tempRefreshedTimer = window.setTimeout(() => {
      setRefreshed(false);
    }, 1500);
    setRefreshedTimer(tempRefreshedTimer);
  };

  // does not trigger a refresh when the current time window is selected again
  useEffect(() => {
    getLabelerMetricsData();
  }, [timeAgoHours, getLabelerMetricsData]);

  return (
    <Container>
      <Stack className="flex-row border-b-2 mb-8">
        <Typography variant="main" className="mr-2">
          Labeler Metrics
        </Typography>
        {refreshed ? (
          <Fade in={refreshed}>
            <Tooltip title="Refreshed!">
              <IconButton onClick={() => handleRefresh()}>
                <CheckIcon style={{ color: "green" }} />
              </IconButton>
            </Tooltip>
          </Fade>
        ) : (
          <Tooltip title="Refresh">
            <IconButton onClick={() => handleRefresh()}>
              <RefreshIcon className="text-primary-bright" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      <Stack className="flex-row space-x-6">
        <FormControl className="w-32 mb-10">
          <InputLabel id="demo-simple-select-label">Time Range</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            value={timeAgoHours}
            label="Time Range"
            onChange={(event) => setTimeAgoHours(Number(event.target.value))}
          >
            <MenuItem value={1}>Last Hour</MenuItem>
            <MenuItem value={24}>Last Day</MenuItem>
            <MenuItem value={168}>Last Week</MenuItem>
          </Select>
        </FormControl>
        <Stack>
          <Stack className="flex-row">
            <Typography variant="subtitle" className="self-center mr-3">Average time to label:</Typography>
            <Typography variant="subtitle" className="self-center text-hl-purple ">
              {labelerMetrics && labelerMetrics.average_elapsed_screen_time_ms >= 0
                ? `${Math.round((labelerMetrics.average_elapsed_screen_time_ms + Number.EPSILON) / 10) / 100} seconds`
                : "No data"}
            </Typography>
          </Stack>
          <Stack className="flex-row">
            <Typography variant="subtitle" className="self-center mr-3">Number of labels:</Typography>
            <Typography variant="subtitle" className=" self-center text-hl-magenta">
              {labelerMetrics ? `${labelerMetrics.label_count} labels` : "No data"}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  );
}
